import { useState } from "react";
import styled from "styled-components/macro";
import DownloadIcon from "@mui/icons-material/GetApp";
import { IconButton, Menu, Tooltip } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { CSVLink } from "react-csv";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  useFocusRange,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import { isEqual, startCase } from "lodash";
import { format } from "date-fns";
import { useCopyClipboard } from "@lokibai/react-use-copy-clipboard";
import { Check, ContentCopy, MoreVert } from "@mui/icons-material";
import {
  DownloadPnlChartImageButton,
  DownloadPnlChartImageMenuItem,
  StyledCardControlsMenuItem,
} from "pages/SnapshotsPage/SnapshotElementButton";
import { useTrendsPageCsvData } from "components/FlightTrends/TrendsPageTopBar";

const PnlChartCardControls = () => {
  return (
    <ControlsStyles className="doNotPrint">
      <PnlChartMoreMenu />
      <div style={{ visibility: "hidden", pointerEvents: "none" }}>
        {/* handles autodownload query param for pnl chart */}
        <DownloadPnlChartImageButton />
      </div>
    </ControlsStyles>
  );
};
const ControlsStyles = styled.div``;

export default PnlChartCardControls;

function PnlChartMoreMenu() {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = event => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const { translate } = useFlightTranslation();

  return (
    <>
      <Tooltip title={translate("More_Options")} placement="bottom-start">
        <IconButton
          className="btnMoreOptions"
          size="small"
          onClick={handleClick}
        >
          <MoreVert />
        </IconButton>
      </Tooltip>

      <Menu
        id="pnl-chart-card-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableScrollLock={true}
      >
        <PnlChartCSVCopyClipboardMenuItem {...{ handleClose }} />
        <PnlChartCSVDownloadMenuItem {...{ handleClose }} />
        <DownloadPnlChartImageMenuItem
          handleClose={() => {
            setAnchorEl(null);
          }}
        />
      </Menu>
    </>
  );
}

function PnlChartCSVDownloadMenuItem({
  handleClose,
}: {
  handleClose: (event: MouseEvent) => void;
}) {
  const ckey = useCustomerKey();
  const focusRange = useFocusRange();
  const { translate } = useFlightTranslation();
  const trendsPageCsvData = useTrendsPageCsvData();
  const visibleGroups = useVisibleGroups();
  if (!focusRange) {
    return null;
  }
  const { ge, lt } = focusRange;
  const fileName = `${startCase(ckey)}__${visibleGroups
    .map(g => g.shortName)
    .join("_")}__${format(ge, "yyyy_MM_dd")}-${format(lt, "yyyy_MM_dd")}`;
  return (
    <CSVLink
      filename={fileName}
      data={trendsPageCsvData?.pnlBreakdownCsvData ?? []}
      style={{ color: "black", textDecoration: "none" }}
    >
      <StyledCardControlsMenuItem onClick={handleClose}>
        <div>
          <IconButton>
            <DownloadIcon />
          </IconButton>
        </div>
        <div style={{ marginRight: 8 }}>{translate("Download_CSV")}</div>
      </StyledCardControlsMenuItem>
    </CSVLink>
  );
}

const COLUMN_NAMES = [
  "type",
  "title",
  "time",
  "percent",
  "category",
  "color",
  "outputLos",
  "numberOfEvents",
  "averageDurationMs",
];
function PnlChartCSVCopyClipboardMenuItem({
  handleClose,
}: {
  handleClose: (event: MouseEvent) => void;
}) {
  const { translate } = useFlightTranslation();
  const trendsPageCsvData = useTrendsPageCsvData();
  const [copiedValue, setCopiedValue] = useState(null as any);
  const [, setCopied] = useCopyClipboard(
    trendsPageCsvData?.pnlBreakdownCsvData ?? []
  );

  const csvStringFromDataRows = trendsPageCsvData?.pnlBreakdownCsvData?.reduce(
    (acc, row) => {
      const rowString = Object.values(row).join(",");
      return acc + rowString + "\n";
    },
    COLUMN_NAMES.join(",") + "\n"
  );

  // copy share URL to clipboard
  const handleCopyClipboard = () => {
    setCopied(csvStringFromDataRows);
    setCopiedValue(csvStringFromDataRows);
    setTimeout(() => setCopiedValue(null), 2500);
  };
  const copied = isEqual(csvStringFromDataRows, copiedValue);

  return (
    <StyledCardControlsMenuItem
      onClick={e => {
        e.stopPropagation();
        handleCopyClipboard();
        setTimeout(() => {
          handleClose(e);
        }, 500);
      }}
    >
      <div>
        <IconButton>{copied ? <Check /> : <ContentCopy />}</IconButton>
      </div>
      <div style={{ marginRight: 8 }}>
        {copied ? translate("Copied") + "!" : translate("Copy_CSV")}
      </div>
    </StyledCardControlsMenuItem>
  );
}
